import React from 'react';
import {
  Cell,
  Legend,
  Pie,
  PieChart as RechartsPieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';

import styled from '@emotion/styled';

type PieDataPoint = {
  name: string;
  value: number;
  color?: string;
  textColor?: string;
  legendLabel?: string;
  legendValue?: string | number;
  hideInLegend?: boolean;
};

interface LegendPayloadItem {
  color?: string;
  payload?: PieDataPoint;
  value?: string;
}

interface PieTooltipProps {
  active?: boolean;
  payload?: Array<{
    name?: string;
    value?: number;
    color?: string;
    payload?: PieDataPoint;
  }>;
  valueFormatter?: (value: number, itemName: string) => React.ReactNode;
}

export interface PieChartProps {
  data?: PieDataPoint[];
  aspect?: number;
  maxHeight?: number;
  innerRadius?: number;
  outerRadius?: number;
  startAngle?: number;
  endAngle?: number;
  cx?: string | number;
  cy?: string | number;
  minAngle?: number;
  paddingAngle?: number;
  cornerRadius?: number;
  showLabels?: boolean;
  showTooltip?: boolean;
  showLegend?: boolean;
  showPercentInLabel?: boolean;
  decimalPlaces?: number;
  labelFormatter?: (args: {
    name: string;
    value: number;
    percent: number;
    index: number;
  }) => string;
  legendAlign?: 'left' | 'center' | 'right';
  legendVerticalAlign?: 'top' | 'middle' | 'bottom';
  legendLayout?: 'horizontal' | 'vertical';
  legendIconSize?: number;
  legendLabelFormatter?: (item: PieDataPoint) => React.ReactNode;
  legendValueFormatter?: (item: PieDataPoint) => React.ReactNode;
  tooltipValueFormatter?: (value: number, itemName: string) => React.ReactNode;
}

const fallbackColors = ['#5B91F5', '#00B73F', '#DEE9FD', '#D9D9D9', '#CA8A04'];
const DEGREE_TO_RADIAN = Math.PI / 180;
const defaultNoDataSlice: PieDataPoint = {
  name: 'no data',
  value: 0,
  color: '#9da3af',
  textColor: 'white',
  legendValue: 0,
  hideInLegend: true,
};

const defaultLabelFormatter = ({
  percent,
  decimalPlaces = 1,
}: {
  percent: number;
  decimalPlaces?: number;
}): string => `${(percent * 100).toFixed(decimalPlaces)}%`;

const PieTooltip: React.FC<PieTooltipProps> = ({
  active,
  payload,
  valueFormatter,
}) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  return (
    <TooltipContainer>
      {payload.map((item) => {
        const name = item.name ?? '';
        const numericValue = Number(item.payload?.value ?? item.value ?? 0);
        const value = Number.isNaN(numericValue) ? 0 : numericValue;

        return (
          <TooltipRow key={`${name}-${value}`}>
            <TooltipDot color={item.color ?? '#9CA3AF'} />
            <TooltipLabel>{name}</TooltipLabel>
            <TooltipValue>
              {valueFormatter ? valueFormatter(value, name) : value}
            </TooltipValue>
          </TooltipRow>
        );
      })}
    </TooltipContainer>
  );
};

const PieLegend: React.FC<{
  payload?: LegendPayloadItem[];
  labelFormatter?: (item: PieDataPoint) => React.ReactNode;
  valueFormatter?: (item: PieDataPoint) => React.ReactNode;
}> = ({ payload, labelFormatter, valueFormatter }) => {
  if (!payload || payload.length === 0) {
    return null;
  }

  const items = payload
    .map((entry) => entry.payload)
    .filter((item): item is PieDataPoint =>
      Boolean(item && !item.hideInLegend),
    );

  if (items.length === 0) {
    return null;
  }

  return (
    <LegendContainer>
      {items.map((item) => (
        <LegendRow key={item.name}>
          <LegendLabelBlock>
            <LegendDot color={item.color ?? '#9CA3AF'} />
            <LegendLabel>
              {labelFormatter
                ? labelFormatter(item)
                : item.legendLabel || item.name}
            </LegendLabel>
          </LegendLabelBlock>

          <LegendValue>
            {valueFormatter
              ? valueFormatter(item)
              : (item.legendValue ?? item.value)}
          </LegendValue>
        </LegendRow>
      ))}
    </LegendContainer>
  );
};

const PieChart: React.FC<PieChartProps> = ({
  data = [],
  aspect = 1.45,
  maxHeight = 420,
  innerRadius = 0,
  outerRadius = 130,
  startAngle = 90,
  endAngle = -270,
  cx = '50%',
  cy = '45%',
  minAngle = 0,
  paddingAngle = 0,
  cornerRadius = 0,
  showLabels = true,
  showTooltip = true,
  showLegend = true,
  showPercentInLabel = true,
  decimalPlaces = 1,
  labelFormatter,
  legendAlign = 'center',
  legendVerticalAlign = 'bottom',
  legendLayout = 'horizontal',
  legendIconSize = 10,
  legendLabelFormatter,
  legendValueFormatter,
  tooltipValueFormatter,
}) => {
  const safeInputData: PieDataPoint[] = Array.isArray(data) ? data : [];

  const parsedData = safeInputData.map((item, index) => ({
    ...item,
    color: item.color ?? fallbackColors[index % fallbackColors.length],
    value: Number(item.value || 0),
    renderValue: Number(item.value || 0),
  }));

  const hasRenderableData = parsedData.some((item) => item.value > 0);
  const safeData = hasRenderableData
    ? parsedData
    : [{ ...defaultNoDataSlice, renderValue: 1 }];
  const isEmptyState = !hasRenderableData;

  const total = Array.isArray(safeData)
    ? safeData.reduce((sum, item) => sum + Number(item.value || 0), 0)
    : 0;

  const renderLabel = (labelProps: any) => {
    const {
      name = '',
      value = 0,
      percent = 0,
      index = 0,
      cx: labelCx = 0,
      cy: labelCy = 0,
      midAngle = 0,
      innerRadius: labelInnerRadius = 0,
      outerRadius: labelOuterRadius = 0,
    } = labelProps;

    if (percent <= 0) {
      return null;
    }

    const radius =
      labelInnerRadius + (labelOuterRadius - labelInnerRadius) * 0.5;
    const x = labelCx + radius * Math.cos(-midAngle * DEGREE_TO_RADIAN);
    const y = labelCy + radius * Math.sin(-midAngle * DEGREE_TO_RADIAN);

    const displayPercent = isEmptyState ? 0 : percent;

    const customText = labelFormatter
      ? labelFormatter({ name, value, percent: displayPercent, index })
      : showPercentInLabel
        ? defaultLabelFormatter({ percent: displayPercent, decimalPlaces })
        : String(value);

    return (
      <text
        x={x}
        y={y}
        fill={safeData[index]?.textColor ?? '#ffffff'}
        textAnchor='middle'
        dominantBaseline='central'
        fontSize='13px'
        fontWeight={500}
      >
        {customText}
      </text>
    );
  };

  return (
    <ChartRoot>
      <ResponsiveContainer width='100%' aspect={aspect} maxHeight={maxHeight}>
        <RechartsPieChart>
          <Pie
            data={safeData}
            dataKey='renderValue'
            nameKey='name'
            cx={cx}
            cy={cy}
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            startAngle={startAngle}
            endAngle={endAngle}
            minAngle={minAngle}
            paddingAngle={paddingAngle}
            cornerRadius={cornerRadius}
            label={showLabels ? renderLabel : false}
            labelLine={false}
            isAnimationActive
            rootTabIndex={-1}
          >
            {safeData.map((entry, index) => (
              <Cell key={`slice-${entry.name}-${index}`} fill={entry.color} />
            ))}
          </Pie>

          {showTooltip && (
            <Tooltip
              content={<PieTooltip valueFormatter={tooltipValueFormatter} />}
            />
          )}

          {showLegend && total > 0 && (
            <Legend
              align={legendAlign}
              verticalAlign={legendVerticalAlign}
              layout={legendLayout}
              iconType='square'
              iconSize={legendIconSize}
              content={
                <PieLegend
                  labelFormatter={legendLabelFormatter}
                  valueFormatter={legendValueFormatter}
                />
              }
            />
          )}
        </RechartsPieChart>
      </ResponsiveContainer>
    </ChartRoot>
  );
};

export default PieChart;

const ChartRoot = styled.div`
  .recharts-wrapper:focus,
  .recharts-wrapper:focus-visible,
  .recharts-sector:focus,
  .recharts-sector:focus-visible,
  .recharts-surface:focus,
  .recharts-surface:focus-visible {
    outline: none;
  }
`;

const LegendContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const LegendLabelBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const LegendDot = styled.span<{ color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background-color: ${({ color }) => color};
  flex-shrink: 0;
`;

const LegendLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.neutralGray};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LegendValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.logirateBlack};
`;

const TooltipContainer = styled.div`
  background: ${({ theme }) => theme.palette.whiteBg};
  border-radius: 10px;
  padding: 10px 14px;
  box-shadow: 0px 6px 16px ${({ theme }) => theme.palette.boxShadowColor};
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TooltipDot = styled.span<{ color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${({ color }) => color};
  flex-shrink: 0;
`;

const TooltipLabel = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.palette.secondaryTextColor};
`;

const TooltipValue = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.logirateBlack};
  margin-left: auto;
`;
